import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CircularProgress, Box } from '@mui/material';

import Layout from './components/layout/Layout';
import theme from './styles/theme';
import ErrorBoundary from './components/common/ErrorBoundary';
import { authService } from './services/authService';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeAdd from './pages/EmployeeAdd';
import EmployeeCheckInOutList from './pages/EmployeeCheckInOutList';
import Projects from './pages/Projects';
import ProjectAdd from './pages/ProjectAdd';
import ProjectEdit from './pages/ProjectEdit';
import ProjectDetails from './pages/ProjectDetails';
import ProjectManagement from './pages/ProjectManagement';
import ProjectEmployeeAdd from './pages/ProjectEmployeeAdd';
import Documents from './pages/Documents';
import Learning from './pages/Learning';
import LearningAdd from './pages/LearningAdd';
import LearningEdit from './pages/LearningEdit';
import KnowledgeCategories from './pages/KnowledgeCategories';
import Users from './pages/Users';
import UserAdd from './pages/UserAdd';
import UserEdit from './pages/UserEdit';
import UserChangePassword from './pages/UserChangePassword';
import CheckInOut from './pages/CheckInOut';
import CheckInOutNew from './pages/CheckInOutNew';
// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if token exists
        if (authService.isAuthenticated()) {
          // Verify token by fetching current user
          await authService.getCurrentUser();
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        // If error (like 401), set as not authenticated
        console.error('Authentication check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          {isAuthenticated ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/checkinout" element={<CheckInOutNew />} />
                {/* <Route path="/checkinoutnew" element={<CheckInOut />} /> */}
                <Route path="/employees" element={<Employees />} />
                <Route path="/employees/add" element={<EmployeeAdd />} />
                <Route path="/employees/:username/checkinout" element={<EmployeeCheckInOutList />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/add" element={<ProjectAdd />} />
                <Route path="/projects/:id" element={<ProjectDetails />} />
                <Route path="/projects/:id/edit" element={<ProjectEdit />} />
                <Route path="/projects/:id/manage" element={<ProjectManagement />} />
                <Route path="/projects/:id/employees/add" element={<ProjectEmployeeAdd />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/learning" element={<Learning />} />
                <Route path="/learning/add" element={<LearningAdd />} />
                <Route path="/learning/:id" element={<Documents />} />
                <Route path="/learning/edit/:id" element={<LearningEdit />} />
                <Route path="/learning/categories" element={<KnowledgeCategories />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/add" element={<UserAdd />} />
                <Route path="/users/:username/edit" element={<UserEdit />} />
                <Route path="/users/:username/change-password" element={<UserChangePassword />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          )}
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
